import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";
import { cn } from "@/lib/utils";
import { useDialogLoadingGuard } from "./dialog-utils";

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
  title: string;
  confirmMessage?: string;
  warningMessage?: string;
  detailsContent: ReactNode;
  showCloseButton?: boolean;
  variant?: "danger" | "default";
  showHeaderIcon?: boolean;
  contentClassName?: string;
  confirmLabel?: string;
  confirmLoadingText?: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
}

export const ConfirmDialog = ({
  open,
  onOpenChange,
  onConfirm,
  loading = false,
  title,
  confirmMessage,
  warningMessage,
  detailsContent,
  showCloseButton = false,
  variant = "danger",
  showHeaderIcon = true,
  contentClassName = "p-4 sm:p-6",
  confirmLabel = "Eliminar",
  confirmLoadingText = "Eliminando...",
  cancelLabel = "Cancelar",
  confirmDisabled = false,
}: ConfirmDialogProps) => {
  const { handleOpenChange } = useDialogLoadingGuard(onOpenChange, loading);

  const headerIcon = showHeaderIcon ? (
    <div className="w-10 h-10 rounded-full bg-global-error/20 flex items-center justify-center">
      <AlertTriangle className="w-5 h-5 text-global-error" />
    </div>
  ) : undefined;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={showCloseButton}>
        <DialogHeader>
          <div className="flex items-center gap-3">
            {headerIcon}
            <DialogTitle>{title}</DialogTitle>
          </div>
        </DialogHeader>

        <div className={cn("overflow-y-auto flex-1", contentClassName)}>
          {confirmMessage && (
            <p className="text-global-label mb-2 font-semibold">
              {confirmMessage}
            </p>
          )}
          {warningMessage && (
            <p className="text-global-secondary text-sm mb-4">
              {warningMessage}
            </p>
          )}
          {detailsContent}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            disabled={loading}
            onClick={() => onOpenChange(false)}
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === "danger" ? "destructive" : "default"}
            className="flex-1"
            disabled={loading || confirmDisabled}
            isLoading={loading}
            onClick={() => onConfirm()}
          >
            {loading ? confirmLoadingText : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
